import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { streamChat } from '../../../services/chat.service';
import { createApiClient } from '../../../services/axios-instance';
import type { ChatMessage, ChatContext, StreamContextType } from '../types';

const StreamContext = createContext<StreamContextType | undefined>(undefined);

export function useStreamContext() {
  const context = useContext(StreamContext);
  if (!context) {
    throw new Error('useStreamContext must be used within StreamProvider');
  }
  return context;
}

interface StreamProviderProps {
  children: React.ReactNode;
  chatEndpoint: string;
  context: ChatContext;
  onMessage?: (message: ChatMessage) => void;
  onError?: (error: string) => void;
}

/**
 * Universal StreamProvider - works with any backend endpoint
 *
 * @param chatEndpoint - Base chat endpoint path (e.g., "/api/v1/<service>/<resource>/123/chat")
 * @param context - Flexible context object (documentId, workspaceId, etc.)
 *
 * Auto-derives three endpoints:
 * - POST {chatEndpoint}/stream - for streaming chat
 * - GET {chatEndpoint}/history - for loading history
 * - DELETE {chatEndpoint}/history - for clearing history
 *
 * @example
 * // Document chat
 * <StreamProvider
 *   chatEndpoint="/api/v1/<service>/<resource>/123/chat"
 *   context={{ selected_text: "", start_line: 0, end_line: 0 }}
 * >
 *
 * // General chat
 * <StreamProvider
 *   chatEndpoint="/api/v1/chat"
 *   context={{ workspaceId: "abc" }}
 * >
 */
export const StreamProvider: React.FC<StreamProviderProps> = ({
  children,
  chatEndpoint,
  context,
  onMessage,
  onError: onErrorProp,
}) => {
  const { idToken, currentWorkspace } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const submit = useCallback(
    async (input: { messages: ChatMessage[] }) => {
      if (!idToken) {
        const errorMsg = 'Not authenticated';
        setError(errorMsg);
        onErrorProp?.(errorMsg);
        return;
      }

      // Get the last human message
      const lastMessage = input.messages[input.messages.length - 1];
      if (!lastMessage || lastMessage.type !== 'human') {
        const errorMsg = 'Invalid message format';
        setError(errorMsg);
        onErrorProp?.(errorMsg);
        return;
      }

      // Extract text content from message
      let messageText = '';
      if (typeof lastMessage.content === 'string') {
        messageText = lastMessage.content;
      } else if (Array.isArray(lastMessage.content)) {
        messageText = lastMessage.content
          .map((c) => {
            if (typeof c === 'string') return c;
            if ('text' in c) return c.text;
            return '';
          })
          .join('');
      }

      if (!messageText.trim()) {
        const errorMsg = 'Message is empty';
        setError(errorMsg);
        onErrorProp?.(errorMsg);
        return;
      }

      setIsLoading(true);
      setError(null);

      // Add human message to UI immediately
      setMessages((prev) => [...prev, lastMessage]);
      onMessage?.(lastMessage);

      // Build request body - merge message with context
      const requestBody = {
        message: messageText,
        ...context, // documentId, selected_text, workspaceId, etc.
      };

      // Accumulate AI response
      let aiResponseContent = '';
      let currentContentType: 'text' | 'diff' = 'text';
      const aiMessageId = `ai-${Date.now()}`;

      // Derive stream endpoint (streamChat uses fetch with baseUrl, needs /api/v1 prefix)
      const streamEndpoint = `/api/v1${chatEndpoint}/stream`;

      try {
        await streamChat(streamEndpoint, requestBody, idToken, {
          // onChunk - update AI message incrementally
          onChunk: (chunk: string, contentType: 'text' | 'diff' = 'text') => {
            aiResponseContent += chunk;
            currentContentType = contentType;

            // Update or add AI message in messages array
            setMessages((prev) => {
              const lastMsg = prev[prev.length - 1];

              // If last message is the AI response we're building, update it
              if (lastMsg && lastMsg.id === aiMessageId && lastMsg.type === 'ai') {
                const updatedMessage: ChatMessage = {
                  ...lastMsg,
                  contentType: currentContentType,
                  content: [{ type: 'text', text: aiResponseContent }],
                };
                return [...prev.slice(0, -1), updatedMessage];
              }

              // Otherwise, add new AI message
              const newMessage: ChatMessage = {
                id: aiMessageId,
                type: 'ai',
                contentType: currentContentType,
                content: [{ type: 'text', text: aiResponseContent }],
              };
              return [...prev, newMessage];
            });
          },

          // onComplete
          onComplete: (metadata: any) => {
            console.log('Chat stream completed:', metadata);
            setIsLoading(false);

            // Notify parent with final AI message
            if (aiResponseContent) {
              const finalMessage: ChatMessage = {
                id: aiMessageId,
                type: 'ai',
                contentType: currentContentType,
                content: [{ type: 'text', text: aiResponseContent }],
                metadata,
              };
              onMessage?.(finalMessage);
            }
          },

          // onError
          onError: (errorMsg: string) => {
            console.error('Chat stream error:', errorMsg);
            setError(errorMsg);
            setIsLoading(false);
            onErrorProp?.(errorMsg);
          },
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);
        setIsLoading(false);
        onErrorProp?.(errorMessage);
      }
    },
    [idToken, chatEndpoint, context, onMessage, onErrorProp]
  );

  const clearHistory = useCallback(async () => {
    if (!idToken || !currentWorkspace?.id) {
      const errorMsg = 'Not authenticated';
      setError(errorMsg);
      onErrorProp?.(errorMsg);
      return;
    }

    // Derive history endpoint from base chat endpoint
    const historyEndpoint = `${chatEndpoint}/history`;

    try {
      const client = createApiClient(idToken);
      await client.delete(historyEndpoint);

      // Clear local messages and reset state
      setMessages([]);
      setHistoryLoaded(false);
      setError(null);

      console.log('Chat history cleared successfully');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to clear history';
      console.error('Error clearing chat history:', err);
      setError(errorMessage);
      onErrorProp?.(errorMessage);
    }
  }, [idToken, currentWorkspace, chatEndpoint, onErrorProp]);

  // Load conversation history on mount
  useEffect(() => {
    const loadHistory = async () => {
      if (!idToken || !currentWorkspace?.id || historyLoaded) return;

      // Derive history endpoint from base chat endpoint
      const historyEndpoint = `${chatEndpoint}/history`;

      try {
        const client = createApiClient(idToken);
        const response = await client.get(historyEndpoint);

        const data = response.data;

        // Convert history to ChatMessage format
        const historyMessages: ChatMessage[] = data.messages.map(
          (msg: { role: string; content: string; content_type?: string }, idx: number) => ({
            id: `history-${idx}`,
            type: msg.role === 'user' ? 'human' : 'ai',
            contentType: (msg.content_type || 'text') as 'text' | 'diff',
            content: [{ type: 'text', text: msg.content }],
          })
        );

        console.log(`Loaded ${historyMessages.length} messages from conversation history`);
        setMessages(historyMessages);
        setHistoryLoaded(true);
      } catch (err) {
        console.error('Error loading chat history:', err);
        setHistoryLoaded(true);
      }
    };

    loadHistory();
  }, [chatEndpoint, idToken, currentWorkspace, historyLoaded]);

  const value: StreamContextType = {
    messages,
    isLoading,
    error,
    submit,
    clearHistory,
    isModalOpen,
    setIsModalOpen,
  };

  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
};
